import { useFormikContext } from 'formik';

import { NewAddress } from '@core/types/api/Address/Address';
import { hasStringValue, hasValue } from '@core/utils/equality';
import { SetFormikValue } from '@core/utils/formikHelpers';
import {
  type PlantDistanceSection,
  usePlantDistanceCalculationShared,
} from '@web/hooks/plantsDistance/usePlantDistanceCalculationShared';
import { useSlabQuery } from '@web/hooks/useSlabQuery';

import type { QuoteDrawerFormikType } from './utils';

// Spelling the prefixes out as a template literal union lets SetFormikValue type check
// the full field path, so a renamed or misspelled field fails to compile.
type ProductsByPlantsFieldPathPrefix =
  | `mainProductsByPlants.${number}`
  | `additionalProductsByPlants.${number}`;

// This hook listens to the project address and plant changes and calculates the distance
// between the address and the plants and updates the formik values for the Quote
export const usePlantDistanceCalculationForQuotes = (): void => {
  const formikBag = useFormikContext<QuoteDrawerFormikType>();

  // Get the project to access its address.
  // `enabled` is load-bearing: without it the query still runs with an empty id,
  // which resolves to the collection URL and parses into a zero-value Project whose
  // address is a non-null object with null coordinates.
  const projectId = formikBag.values.project?.id ?? '';
  const shouldFetchProject = hasStringValue(projectId);
  const { data: project } = useSlabQuery(
    'GET project by ID',
    { pathParams: { id: projectId } },
    { enabled: shouldFetchProject },
  );

  // Collect all plants from both main and additional products
  const plantSections: PlantDistanceSection<ProductsByPlantsFieldPathPrefix>[] = [];

  formikBag.values.mainProductsByPlants?.forEach((plantSection, index) => {
    plantSections.push({
      plantId: plantSection.plant?.id ?? null,
      formikPath: `mainProductsByPlants.${index}`,
      products: plantSection.products,
    });
  });

  formikBag.values.additionalProductsByPlants?.forEach((plantSection, index) => {
    plantSections.push({
      plantId: plantSection.plant?.id ?? null,
      formikPath: `additionalProductsByPlants.${index}`,
      products: plantSection.products,
    });
  });

  // Quick Quote with no project: fall back to the address the rep typed into
  // the QQ form. NewAddress wraps the DomainObject<Address> so we can reuse
  // toLatLngPairString and avoid duplicating coordinate parsing. Only fall back
  // when the rep is in 'new' project mode — otherwise a stale quickAddress from
  // an earlier toggle would keep driving plant sort after switching to
  // 'existing' before picking a project.
  const isNewProjectMode = formikBag.values.quickProjectMode === 'new';
  const hasQuickAddress =
    isNewProjectMode &&
    hasValue(formikBag.values.quickAddress?.latitude) &&
    hasValue(formikBag.values.quickAddress?.longitude);

  // A project address is only usable as a distance origin once it has coordinates;
  // an un-geocoded one would otherwise win the `??` below (it is a non-null object)
  // and silently stall every recalculation.
  const projectAddress =
    hasValue(project?.address?.latitude) && hasValue(project?.address?.longitude)
      ? project?.address
      : undefined;

  const effectiveAddress =
    projectAddress ?? (hasQuickAddress ? NewAddress(formikBag.values.quickAddress) : undefined);

  usePlantDistanceCalculationShared({
    effectiveAddress,
    plantSections,
    setFieldValue: formikBag.setFieldValue,
    setPlantFields: (formikPath, { distanceInUnit, driveTimeISO, unit }) => {
      SetFormikValue(
        formikBag,
        `${formikPath}.plantDistance`,
        hasValue(distanceInUnit) ? String(distanceInUnit) : null,
      );
      SetFormikValue(formikBag, `${formikPath}.plantDistanceUnit`, unit);
      SetFormikValue(formikBag, `${formikPath}.plantDriveTime`, driveTimeISO);
    },
  });
};
